import IconButton from '@/components/IconButton/IconButton';
import { styled } from '@mui/material/styles';
import { ReactNode, useEffect, useRef, useState } from 'react';
import NavArrowDownIcon from '@/assets/nav-arrow-down.svg?react';

const Section = ({
  children,
  title,
  titleAction,
  'aria-label': ariaLabel,
  mode = 'light',
  defaultOpen = false,
  onToggle,
}: {
  children: ReactNode;
  title: ReactNode;
  titleAction?: ReactNode;
  'aria-label'?: string;
  mode?: 'light' | 'dark';
  defaultOpen?: boolean;
  onToggle?: (open: boolean) => void;
}) => {
  const bodyRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(defaultOpen);
  const [height, setHeight] = useState<number | null>(null);

  const handleToggle = () => {
    const nextOpen = !open;
    setOpen(nextOpen);
    onToggle?.(nextOpen);
  };

  useEffect(() => {
    if (bodyRef?.current?.scrollHeight) {
      setHeight(bodyRef?.current?.scrollHeight);
    }
  }, [children]);

  return (
    <Root aria-label={ariaLabel}>
      <Title>
        <TitleLabel>{title}</TitleLabel>
        {titleAction || (
          <TitleAction>
            <IconButton
              onClick={handleToggle}
              mode={mode}
              aria-label={`${open ? 'Collapse' : 'Expand'} ${
                ariaLabel || 'section'
              }`}
            >
              <StyledArrowIcon isOpen={!open} />
            </IconButton>
          </TitleAction>
        )}
      </Title>
      <Body
        isOpen={open}
        ref={bodyRef}
        style={{ maxHeight: open && height !== null ? height : 0 }}
      >
        {children}
      </Body>
    </Root>
  );
};

const Root = styled('div')(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.grey200}`,
  paddingBottom: 17,
}));

const Title = styled('div')({
  display: 'flex',
  alignItems: 'center',
  paddingTop: 18,
});

const TitleLabel = styled('div')({
  flexGrow: 1,
  fontSize: 16,
  lineHeight: '24px',
  fontWeight: 500,
});

const TitleAction = styled('div')({});

const StyledArrowIcon = styled(NavArrowDownIcon, {
  shouldForwardProp(propName) {
    return propName !== 'isOpen';
  },
})<{ isOpen: boolean }>(({ isOpen }) => ({
  transition: 'transform 0.3s ease',
  transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
}));

const Body = styled('div')<{ isOpen: boolean }>(({ isOpen }) => ({
  display: 'flex',
  flexDirection: 'column',
  transition: 'max-height 0.3s ease-in-out',
  maxHeight: isOpen ? '260px' : 0,
  overflow: 'hidden',
}));

export default Section;
